import React from 'react';
import PropTypes from 'prop-types';

const GradientButton = ({
  children,
  variant = 'solid',
  size = 'md',
  color = 'primary',
  icon = null,
  disabled = false,
  loading = false,
  fullWidth = false,
  glow = false,
  className = '',
  ...domProps
}) => {
  // Base classes for all buttons
  const baseClasses = [
    'inline-flex items-center justify-center gap-2',
    'rounded-lg font-medium transition-all duration-300',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    loading ? 'cursor-wait' : '',
    fullWidth ? 'w-full' : '',
    glow ? 'shadow-lg hover:shadow-xl' : ''
  ];

  // Size variations
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };
  baseClasses.push(sizeClasses[size] || sizeClasses.md);

  // Color and variant combinations
  const styles = {
    solid: {
      primary: 'bg-gradient-to-r from-violet-600 to-violet-500 text-white hover:from-violet-500 hover:to-violet-400 focus:ring-violet-500',
      accent: 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 focus:ring-blue-500'
    },
    outline: {
      primary: 'border-2 border-violet-500/50 text-violet-400 hover:bg-violet-500/10 focus:ring-violet-500',
      accent: 'border-2 border-blue-500/50 text-blue-400 hover:bg-blue-500/10 focus:ring-blue-500'
    }
  };
  baseClasses.push(styles[variant]?.[color] || styles.solid.primary);

  // Additional classes
  if (className) {
    baseClasses.push(className);
  }

  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={baseClasses.filter(Boolean).join(' ')}
      {...domProps}
    >
      {loading ? (
        <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        <>
          {icon && <span className="w-5 h-5">{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

GradientButton.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['solid', 'outline']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.oneOf(['primary', 'accent']),
  icon: PropTypes.node,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  glow: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func
};

export default GradientButton;
